if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.content = this.querySelector('[role="document"]');
        this.dialog = this.querySelector('.product-media-modal__dialog');
        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');

        // Prevent clicks inside the dialog (image taps, nav buttons) from
        // triggering the base media-modal "click anywhere to close" behaviour.
        // Closing stays available via the close button and the Escape key.
        if (this.dialog) {
          this.dialog.addEventListener('pointerup', (event) => event.stopPropagation());
        }

        if (this.prevButton) {
          this.prevButton.addEventListener('click', () => this.navigate(-1));
        }
        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => this.navigate(1));
        }

        this.addEventListener('keyup', (event) => {
          if (event.code === 'ArrowRight') this.navigate(1);
          if (event.code === 'ArrowLeft') this.navigate(-1);
        });

        // Keep the arrows in sync with the carousel position (swipe included).
        this.content.addEventListener('scroll', () => {
          if (this.navUpdateFrame) return;
          this.navUpdateFrame = requestAnimationFrame(() => {
            this.navUpdateFrame = null;
            this.updateNav();
          });
        });

        this.setupZoom();

        this.sectionId = this.id.replace('ProductModal-', '');
        this.stickyBar = this.querySelector('[data-zoom-sticky-cart]');
        this.setupStickyCart();
      }

      get allSlides() {
        return Array.from(this.content.querySelectorAll('[data-media-id]'));
      }

      get allImages() {
        return Array.from(this.content.querySelectorAll('img[data-media-id]'));
      }

      // Only the slides for the current colorway take part in the carousel.
      get slides() {
        return this.allSlides.filter(
          (slide) => !slide.classList.contains('product-media-modal__slide--hidden')
        );
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.populateStickyCart();
      }

      setupStickyCart() {
        if (!this.stickyBar) return;
        this.stickyButton = this.stickyBar.querySelector('.product-sticky__submit');
        if (!this.stickyButton) return;

        this.stickyButton.addEventListener('click', () => {
          const mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const needsSize = mainSubmit?.dataset.stickyNeedsSize === 'true';

          this.hide();

          if (needsSize) {
            window.requestAnimationFrame(() => this.scrollToSizePicker());
            return;
          }

          if (mainSubmit && !mainSubmit.disabled) mainSubmit.click();
        });
      }

      findSizeGroup() {
        const productSection = document.getElementById(`MainProduct-${this.sectionId}`);
        if (!productSection) return null;

        const explicitSizeGroup = productSection.querySelector('.product-form__input.size');
        if (explicitSizeGroup) return explicitSizeGroup;

        const sizeInput = productSection.querySelector('input[type="radio"][name="Size"], select[name="options[Size]"]');
        return sizeInput?.closest('.product-form__input') || null;
      }

      scrollToSizePicker() {
        const target = this.findSizeGroup();
        if (!target) return;

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const focusTarget =
          target.querySelector('input[type="radio"]:not(.disabled), select') || target;

        window.setTimeout(() => {
          if (!focusTarget.hasAttribute('tabindex') && focusTarget === target) {
            focusTarget.setAttribute('tabindex', '-1');
          }
          focusTarget.focus({ preventScroll: true });
        }, 350);
      }
      
      populateStickyCart() {
        if (!this.stickyBar) return;
        const info = document.getElementById(`ProductInfo-${this.sectionId}`) || document;

        const priceSource = document.getElementById(`price-${this.sectionId}`);
        const priceTarget = this.stickyBar.querySelector('.product-sticky__price');
        if (priceSource && priceTarget) priceTarget.innerHTML = priceSource.innerHTML;

        const colorSource = info.querySelector('.current-color');
        const captionTarget = this.stickyBar.querySelector('.product-sticky__caption');
        if (captionTarget) {
          const colorText = colorSource ? colorSource.textContent.trim() : '';
          captionTarget.textContent = colorText;
          captionTarget.hidden = !colorText;
        }

        const activeImage = this.content.querySelector('img.active, .active img');
        const imageTarget = this.stickyBar.querySelector('.product-sticky__image img');
        if (activeImage && imageTarget) {
          imageTarget.src = activeImage.currentSrc || activeImage.src;
        }

        const mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (mainSubmit && this.stickyButton) {
          const mainText = mainSubmit.querySelector('span');
          const textTarget = this.stickyButton.querySelector('span');
          const needsSize = mainSubmit.dataset.stickyNeedsSize === 'true';
          if (mainText && textTarget) textTarget.textContent = mainText.textContent.trim();
          this.stickyButton.disabled = mainSubmit.disabled && !needsSize;
        }
      }

      showActiveMedia() {
        const targetId = this.openedBy.getAttribute('data-media-id');
        const activeMedia = this.content.querySelector(`[data-media-id="${targetId}"]`);
        if (!activeMedia) return;

        this.filterByCurrentGallery();

        this.allSlides.forEach((slide) => {
          slide.classList.toggle('active', slide === activeMedia);
        });

        // Jump (no animation) to the slide the shopper clicked.
        const previousBehavior = this.content.style.scrollBehavior;
        this.content.style.scrollBehavior = 'auto';
        this.content.scrollLeft = activeMedia.offsetLeft;
        this.content.style.scrollBehavior = previousBehavior;

        this.updateNav();

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      // The PDP colorway filter owns each slide's inline display state. Do not
      // use computed styles here: thumbnail layouts also hide slides with CSS,
      // even when they belong to the selected colorway.
      filterByCurrentGallery() {
        const gallery = document.getElementById(`Slider-Gallery-${this.sectionId}`);
        if (!gallery) return;

        const visibleMediaIds = new Set(
          Array.from(gallery.querySelectorAll('[data-media-id]'))
            .filter((slide) => slide.style.display !== 'none')
            .map((slide) => slide.dataset.mediaId.replace(`${this.sectionId}-`, ''))
        );

        // Fall back to the complete modal gallery if no media can be resolved
        // from the PDP gallery rather than leaving the shopper with no slides.
        if (!visibleMediaIds.size) return;

        this.allSlides.forEach((slide) => {
          const hide = !visibleMediaIds.has(slide.dataset.mediaId);
          slide.classList.toggle('product-media-modal__slide--hidden', hide);
        });
      }

      navigate(direction) {
        const slides = this.slides;
        if (slides.length < 2) return;

        this.resetZoom();
        const current = this.currentIndex();
        const next = Math.min(Math.max(current + direction, 0), slides.length - 1);
        if (next === current) return;

        slides[next].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }

      currentIndex() {
        const slides = this.slides;
        const viewportCenter = this.content.scrollLeft + this.content.clientWidth / 2;
        let index = 0;
        let smallestDistance = Infinity;

        slides.forEach((slide, i) => {
          const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
          const distance = Math.abs(slideCenter - viewportCenter);
          if (distance < smallestDistance) {
            smallestDistance = distance;
            index = i;
          }
        });

        return index;
      }

      // Hide each arrow once the carousel reaches that end.
      updateNav() {
        if (!this.prevButton && !this.nextButton) return;
        const slides = this.slides;
        const index = this.currentIndex();
        const prevHidden = slides.length < 2 || index <= 0;
        const nextHidden = slides.length < 2 || index >= slides.length - 1;

        let changed = false;
        if (this.prevButton && this.prevButton.hidden !== prevHidden) {
          this.prevButton.hidden = prevHidden;
          changed = true;
        }
        if (this.nextButton && this.nextButton.hidden !== nextHidden) {
          this.nextButton.hidden = nextHidden;
          changed = true;
        }

        // The arrows are focus-trap boundaries; the trap snapshots its first/last
        // focusable element once and never refreshes. Re-run it whenever an arrow's
        // visibility changes so Tab/Shift+Tab can't try to focus a hidden button.
        if (changed) this.refreshFocusTrap();
      }

      refreshFocusTrap() {
        if (!this.hasAttribute('open')) return;
        const active = document.activeElement;
        const keepFocus = active && this.contains(active) ? active : this.dialog;
        trapFocus(this, keepFocus);
      }

      setupZoom() {
        // Expose each image as an accessible toggle button so keyboard and
        // screen-reader users can zoom, not just mouse/touch.
        const zoomLabel = this.getAttribute('data-zoom-label');
        this.allImages.forEach((image) => {
          image.setAttribute('role', 'button');
          image.setAttribute('tabindex', '0');
          image.setAttribute('aria-pressed', 'false');
          if (zoomLabel) image.setAttribute('aria-label', zoomLabel);
        });

        this.content.addEventListener('click', (event) => {
          const image = event.target.closest('img[data-media-id]');
          if (!image) return;
          this.toggleZoom(image, event);
        });

        // Enter/Space on a focused image zooms to its centre (no pointer origin).
        this.content.addEventListener('keydown', (event) => {
          if (event.code !== 'Enter' && event.code !== 'Space') return;
          const image = event.target.closest('img[data-media-id]');
          if (!image) return;
          event.preventDefault();
          this.toggleZoom(image);
        });

        // While zoomed, pan the image by following the pointer.
        this.content.addEventListener('pointermove', (event) => {
          if (!this.zoomedImage || !this.zoomedImage.classList.contains('is-zoomed')) return;
          this.updateOrigin(this.zoomedImage, event);
        });
      }

      toggleZoom(image, event) {
        const zoomed = image.classList.toggle('is-zoomed');
        image.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
        if (zoomed) {
          this.zoomedImage = image;
          if (event) {
            this.updateOrigin(image, event);
          } else {
            image.style.transformOrigin = '';
          }
        } else {
          this.clearOrigin(image);
        }
      }

      updateOrigin(image, event) {
        const rect = image.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        image.style.transformOrigin = `${x}% ${y}%`;
      }

      clearOrigin(image) {
        image.style.transformOrigin = '';
        if (this.zoomedImage === image) this.zoomedImage = null;
      }

      resetZoom() {
        this.allImages.forEach((image) => {
          image.classList.remove('is-zoomed');
          image.style.transformOrigin = '';
          image.setAttribute('aria-pressed', 'false');
        });
        this.zoomedImage = null;
      }
    }
  );
}
